import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { ShieldAlert, Check, Loader2 } from "lucide-react";
import {
  fetchAllergenCatalog,
  fetchAllergenProfile,
  saveAllergenProfile,
} from "../lib/api";

// Map backend allergen.id → i18n key suffix. Backend ships English
// labels/descriptions; we override per-locale here. New allergens
// fall through to the backend label so the picker stays robust to
// catalog additions before translations land.
const ALLERGEN_I18N_MAP = {
  fragrance: "fragrance",
  essential_oils: "essentialOils",
  parabens: "parabens",
  formaldehyde_releasers: "formaldehyde",
  mit_mci: "mit",
  lanolin: "lanolin",
  propylene_glycol: "propylene",
  salicylic_acid: "salicylic",
  castor_oil: "castor",
  nickel: "nickel",
};

/**
 * AllergenPicker — onboarding + settings widget.
 *
 * `mode="onboarding"` shows compact cards + a single-line save button.
 * `mode="settings"`   shows full descriptions + a sticky save bar.
 *
 * On mount it loads the user's saved allergens (when authed) AND the
 * public catalog. The catalog is what's renderable; the saved profile
 * is what's pre-selected. Both calls are independent so a slow auth
 * lookup never blocks the catalog UI.
 */
export default function AllergenPicker({
  mode = "settings",
  onSaved,
  onSkip,
  authed = true,
  initialSelected = null,
}) {
  const { t } = useTranslation();
  const [catalog, setCatalog] = useState(null);
  const [selected, setSelected] = useState(
    () => new Set(initialSelected || []),
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastVerified, setLastVerified] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cat = await fetchAllergenCatalog();
        if (cancelled) return;
        setCatalog(cat.allergens);
        setLastVerified(cat.last_verified);

        if (authed && !initialSelected) {
          try {
            const profile = await fetchAllergenProfile();
            if (cancelled) return;
            setSelected(new Set(profile.allergens || []));
          } catch {
            /* not signed in or no profile yet — selected stays empty */
          }
        }
      } catch {
        toast.error("Couldn't load the allergen list. Please refresh.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authed, initialSelected]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSave = async () => {
    if (!authed) {
      // Onboarding pre-signup — hand the selection back to the parent.
      onSaved?.(Array.from(selected));
      return;
    }
    setSaving(true);
    try {
      const data = await saveAllergenProfile(Array.from(selected));
      toast.success(
        data.allergens.length === 0
          ? "Cleared your allergen flags."
          : `Saved ${data.allergens.length} allergen${
              data.allergens.length === 1 ? "" : "s"
            }.`,
      );
      onSaved?.(data.allergens);
    } catch {
      toast.error("Couldn't save. Try again in a sec.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div
        className="text-sm text-[#9B6A6C] flex items-center gap-2"
        data-testid="allergen-picker-loading"
      >
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading the allergen list…
      </div>
    );
  }

  if (!catalog || catalog.length === 0) {
    return (
      <div className="text-sm text-[#9B6A6C]">
        We couldn't load the list. Please refresh.
      </div>
    );
  }

  const compact = mode === "onboarding";

  return (
    <div data-testid="allergen-picker">
      <div
        className="flex items-start gap-2 mb-4 p-3 bg-[#FDF6F3] border border-[#F2E8E9] rounded-xl text-xs text-[#5C4B51] leading-relaxed"
        data-testid="allergen-picker-disclaimer"
      >
        <ShieldAlert className="h-4 w-4 text-[#9B6A6C] mt-0.5 shrink-0" />
        <div>
          <strong className="text-[#2C2123]">Always check the actual product label.</strong>{" "}
          Brands reformulate; our ingredient list was last verified{" "}
          {lastVerified || "recently"}. This is informational, not medical
          advice — please patch test new products.
        </div>
      </div>

      <div
        className={
          compact
            ? "grid grid-cols-1 sm:grid-cols-2 gap-2"
            : "grid grid-cols-1 gap-2"
        }
      >
        {catalog.map((a) => {
          const on = selected.has(a.id);
          // Translation override — backend ship English labels, we
          // localize where keys exist. Unmapped IDs fall through to
          // the backend label so the picker stays robust.
          const i18nKey = ALLERGEN_I18N_MAP[a.id];
          const label = i18nKey
            ? t(`onboarding.steps.allergens.${i18nKey}`, a.label)
            : a.label;
          const description = i18nKey
            ? t(`onboarding.steps.allergens.${i18nKey}Desc`, a.description)
            : a.description;
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => toggle(a.id)}
              className={`text-left rounded-xl border px-4 py-3 transition-all ${
                on
                  ? "bg-[#9B6A6C]/10 border-[#9B6A6C] text-[#5D4050]"
                  : "bg-white border-[#EADDD7] hover:border-[#9B6A6C]/50 text-[#2C2123]"
              }`}
              aria-pressed={on}
              data-testid={`allergen-toggle-${a.id}`}
            >
              <div className="flex items-start justify-between gap-2 mb-0.5">
                <span className="font-medium text-sm">{label}</span>
                {on && (
                  <Check className="h-4 w-4 text-[#9B6A6C] shrink-0 mt-0.5" />
                )}
              </div>
              {!compact && (
                <p className="text-xs text-[#8A7D82] leading-snug">
                  {description}
                </p>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between gap-3 mt-5 flex-wrap">
        <div className="text-xs text-[#8A7D82]" data-testid="allergen-picker-count">
          {selected.size === 0
            ? "Nothing flagged yet — that's totally fine."
            : `${selected.size} flagged`}
        </div>
        <div className="flex items-center gap-2">
          {onSkip && (
            <button
              type="button"
              onClick={onSkip}
              className="text-sm text-[#8A7D82] hover:text-[#2C2123] underline-offset-4 hover:underline transition-colors"
              data-testid="allergen-picker-skip"
            >
              Skip for now
            </button>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-[#9B6A6C] hover:bg-[#7d5455] text-white rounded-full px-5 py-2.5 text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-wait"
            data-testid="allergen-picker-save"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {compact ? "Continue" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
